import { AxiosInstance } from 'axios';
import {
  createAxiosInstance,
  attachAuthInterceptors,
} from '@/application/helpers/APIRequest';
import type { TopupField } from './topup';
import { compressImageToWebP } from '@/application/utils/imageCompression';

interface BaseResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  code?: string;
  status?: number;
}

export const gameListAPI: AxiosInstance = createAxiosInstance({
  baseUrlKey: 'GAME_CATALOG_API_BASE_URL',
});
attachAuthInterceptors(gameListAPI);

export interface SupplierData {
  id: string;
  supplier_key: string;
  name: string;
  description: string;
  is_active: boolean;
  is_deleted: boolean;
  supported_game_topup_types?: Array<{
    game_topup_type_id: string;
    name: string;
    requires_link: boolean;
    display_name?: string;
    description?: string;
    link_format?: string;
  }>;
  created_at: string;
  updated_at: string;
}

export interface GetSuppliersResponse {
  success: boolean;
  data?: {
    data: SupplierData[];
    total: number;
    limit: number;
    page: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  message?: string;
}

export const getSuppliers = async (params?: {
  page?: number;
  limit?: number;
}): Promise<GetSuppliersResponse> => {
  try {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 10;
    const response = await gameListAPI.get(
      `/admin/suppliers/?page=${page}&limit=${limit}`
    );
    return {
      success: true,
      data: {
        data: response.data.data,
        total: response.data.total,
        limit: response.data.limit,
        page: response.data.page,
        total_pages: response.data.total_pages,
        has_next: response.data.has_next ?? false,
        has_previous: response.data.has_previous ?? false,
      },
      message: 'Suppliers fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch suppliers',
    };
  }
};

export interface GameData {
  id: string;
  game_key: string;
  name: string;
  description: string;
  seo_title?: string;
  seo_description?: string;
  slug?: string;
  image_url: string;
  source: string;
  topup_provider?: string;
  is_active: boolean;
  inactive_reason?: string;
  has_promotion: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  package_count: number;
  created_by_username: string;
  updated_by_username: string;
  topup_type: 'PACKAGE' | 'CHECKOUT_LINK';
  topup_fields?: TopupField[];
  image_file: string | File;
  suppliers?: Array<{
    id: string;
    supplier_key: string;
    name: string;
  }>;
  game_topup_types?: Array<{
    id: string;
    name: string;
    requires_link: boolean;
    topup_fields?: TopupField[];
    title?: string;
    content_html?: string;
  }>;
  topup_options?: Array<{
    supplier_id: string;
    supplier_name: string;
    game_topup_type_id: string;
    topup_type_name: string;
    display_name?: string;
    is_active: boolean;
  }>;
  topup_guides?: Array<TopupGuide>;
}

export interface TopupGuide {
  id: string;
  supplier_id: string;
  supplier_name: string;
  game_topup_type_id: string;
  topup_type_name: string;
  title: string;
  content_html: string;
  /** Some GET responses use this key instead of `content_html` (same as Edit form `readTopupGuideTitleAndContent`). */
  guide_content?: string;
  is_active: boolean;
}

/**
 * Resolved HTML for display. Game catalog GET payloads may use different keys
 * (`content_html`, `guide_content`, or occasional `content` / `body`).
 */
export function getTopupGuideContentHtml(guide: unknown): string {
  if (guide === null || typeof guide !== 'object') return '';
  const g = guide as Record<string, unknown>;
  const raw =
    g.content_html ?? g.guide_content ?? g.content ?? g.guide_body ?? g.body;
  return typeof raw === 'string' ? raw : '';
}

/** Composite key: `{game_topup_type_id}::{supplier_id}` for per-supplier guide state */
export const GUIDE_COMPOSITE_SEP = '::' as const;

export function makeGuideCompositeKey(
  gameTopupTypeId: string,
  supplierId: string
): string {
  return `${String(gameTopupTypeId).trim()}${GUIDE_COMPOSITE_SEP}${String(
    supplierId
  ).trim()}`;
}

export function parseGuideCompositeKey(key: string): {
  typeId: string;
  supplierId: string;
} {
  const s = String(key);
  const i = s.indexOf(GUIDE_COMPOSITE_SEP);
  if (i < 0) {
    return { typeId: s.trim(), supplierId: '' };
  }
  return {
    typeId: s.slice(0, i).trim(),
    supplierId: s.slice(i + GUIDE_COMPOSITE_SEP.length).trim(),
  };
}

export interface GetAllGamesRequest {
  page?: number;
  limit?: number;
  search?: string;
  developer_id?: string;
  is_active?: boolean;
  include_deleted?: boolean;
}

export interface GetAllGamesResponse {
  success: boolean;
  data?: {
    data: GameData[];
    total: number;
    limit: number;
    page: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  message?: string;
}

export const getAllGames = async (
  params: GetAllGamesRequest
): Promise<GetAllGamesResponse> => {
  try {
    const {
      page = 1,
      limit = 10,
      search,
      developer_id,
      is_active,
      include_deleted,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    if (search) queryParams.append('search', search);
    if (developer_id) queryParams.append('developer_id', developer_id);
    if (is_active !== undefined)
      queryParams.append('is_active', is_active.toString());
    if (include_deleted !== undefined)
      queryParams.append('include_deleted', include_deleted.toString());

    const response = await gameListAPI.get(`/games?${queryParams.toString()}`);

    return {
      success: true,
      data: {
        data: response.data.data,
        total: response.data.total,
        limit: response.data.limit,
        page: response.data.page,
        total_pages: response.data.total_pages,
        has_next: response.data.has_next || false,
        has_previous: response.data.has_previous || false,
      },
      message: response.data.message || 'Games fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch games',
    };
  }
};

export interface GameOption {
  id: string;
  name: string;
}
export interface GetAllGamesOptionsResponse {
  success: boolean;
  data?: GameOption[];
  message?: string;
}
export const getAllGamesOptions = async (params?: {
  includeDeleted?: boolean;
  includeInactive?: boolean;
  forTopup?: boolean;
}): Promise<GetAllGamesOptionsResponse> => {
  try {
    const queryParams: Record<string, boolean> = {};
    if (params?.includeDeleted) queryParams.include_deleted = true;
    if (params?.includeInactive) queryParams.include_inactive = true;
    if (params?.forTopup) queryParams.for_topup = true;

    const response = await gameListAPI.get(`/games/options`, {
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    });

    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Games fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch games',
    };
  }
};

/** GET `/games/bulk-order-template` — returns template for bulk topup (CSV or XLSX). */
export const getBulkOrderTemplate = async (
  format: 'csv' | 'xlsx' = 'csv'
): Promise<Blob> => {
  const response = await gameListAPI.get('/games/bulk-order-template', {
    params: { format },
    responseType: 'blob',
  });
  return response.data;
};

/** GET `/games/:id` — axios body มักเป็น `{ data: <GameData> }` หรือ `{ data: { game: <GameData> } }` */
function unwrapGameCatalogGamePayload(body: unknown): GameData | undefined {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) {
    return undefined;
  }
  const b = body as Record<string, unknown>;
  const inner =
    b.data !== undefined &&
    b.data !== null &&
    typeof b.data === 'object' &&
    !Array.isArray(b.data)
      ? (b.data as Record<string, unknown>)
      : b;
  if (Array.isArray(inner)) return undefined;
  if (
    inner.game &&
    typeof inner.game === 'object' &&
    !Array.isArray(inner.game)
  ) {
    return inner.game as unknown as GameData;
  }
  return inner as unknown as GameData;
}

const isAbortLike = (error: unknown) => {
  const e = error as { code?: string; name?: string };
  return (
    e?.code === 'ERR_CANCELED' ||
    e?.name === 'CanceledError' ||
    e?.name === 'AbortError'
  );
};

export const getGameById = async (
  id: string,
  opts?: { signal?: AbortSignal }
): Promise<BaseResponse<GameData>> => {
  try {
    const response = await gameListAPI.get(`/games/${id}`, {
      skipGlobal500ErrorToast: true,
      signal: opts?.signal,
    });
    const payload = unwrapGameCatalogGamePayload(response.data);
    return {
      success: true,
      data: payload,
      message:
        (response.data as { message?: string })?.message ||
        'Game fetched successfully',
    };
  } catch (error: any) {
    if (isAbortLike(error)) throw error;
    return {
      success: false,
      status: error.response?.status as number | undefined,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch game',
    };
  }
};

export interface UpdateGameRequest {
  id: string;
  name: string;
  description: string;
  seo_title?: string;
  seo_description?: string;
  has_promotion: boolean;
  topup_guides?: TopupGuide[];
  image_file: File | string;
  is_active?: boolean;
  supplier_ids?: string[];
  topup_provider?: string;
  game_key?: string;
  slug?: string;
}

export const updateGame = async (
  data: UpdateGameRequest
): Promise<BaseResponse<GameData>> => {
  try {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description);
    formData.append('has_promotion', data.has_promotion.toString());
    if (data.is_active !== undefined) {
      formData.append('is_active', data.is_active.toString());
    }
    if (data.image_file) {
      formData.append(
        'image_file',
        data.image_file instanceof File
          ? await compressImageToWebP(data.image_file, { maxDimension: 1024 })
          : data.image_file
      );
    }
    if (data.supplier_ids && data.supplier_ids.length > 0) {
      formData.append('supplier_ids', JSON.stringify(data.supplier_ids));
    }
    if (data.topup_provider) {
      formData.append('topup_provider', data.topup_provider);
    }
    if (data.game_key != null && String(data.game_key).trim() !== '') {
      formData.append('game_key', String(data.game_key).trim());
    }
    // Always send slug so clearing the field clears the stored slug
    // (backend: absent = unchanged, present-but-empty = clear to NULL)
    formData.append('slug', String(data.slug ?? '').trim());
    if (data.seo_title != null && String(data.seo_title).trim() !== '') {
      formData.append('seo_title', String(data.seo_title).trim());
    }
    if (data.seo_description != null && String(data.seo_description).trim() !== '') {
      formData.append('seo_description', String(data.seo_description).trim());
    }

    const response = await gameListAPI.put(
      `/admin/games/${data.id}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Game fetched successfully',
    };
  } catch (error: any) {
    const code = error.response?.data?.code;
    const status = error.response?.data?.status ?? error.response?.status;
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch game',
      code,
      status,
    };
  }
};

export interface CreateGameRequest {
  name: string;
  image_file: File | string;
  has_promotion: boolean;
  is_active: boolean;
  description?: string;
  seo_title?: string;
  seo_description?: string;
  supplier_ids?: string[];
  topup_provider?: string;
  game_key?: string;
  slug?: string;
}

export const createGame = async (
  data: CreateGameRequest
): Promise<BaseResponse<GameData>> => {
  try {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('has_promotion', data.has_promotion.toString());
    formData.append('description', data.description ?? '');
    formData.append('is_active', data.is_active.toString());
    if (data.image_file) {
      formData.append(
        'image_file',
        data.image_file instanceof File
          ? await compressImageToWebP(data.image_file, { maxDimension: 1024 })
          : data.image_file
      );
    }
    if (data.supplier_ids && data.supplier_ids.length > 0) {
      formData.append('supplier_ids', JSON.stringify(data.supplier_ids));
    }
    if (data.topup_provider) {
      formData.append('topup_provider', data.topup_provider);
    }
    if (data.game_key != null && String(data.game_key).trim() !== '') {
      formData.append('game_key', String(data.game_key).trim());
    }
    if (data.slug != null && String(data.slug).trim() !== '') {
      formData.append('slug', String(data.slug).trim());
    }
    if (data.seo_title != null && String(data.seo_title).trim() !== '') {
      formData.append('seo_title', String(data.seo_title).trim());
    }
    if (data.seo_description != null && String(data.seo_description).trim() !== '') {
      formData.append('seo_description', String(data.seo_description).trim());
    }

    const response = await gameListAPI.post('/admin/games', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Game created successfully',
    };
  } catch (error: any) {
    const code = error.response?.data?.code;
    const status = error.response?.data?.status ?? error.response?.status;
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to create game',
      code,
      status,
    };
  }
};

export interface ResponsePreviewDeleteGame {
  game_id: string;
  game_name: string;
  game_packages_count: number;
  game_servers_count: number;
  serial_codes_count: number;
  total_count: number;
}

export const getPreviewDeleteGame = async (
  id: string
): Promise<ResponsePreviewDeleteGame> => {
  try {
    const response = await gameListAPI.get(`/admin/games/${id}/soft/preview`);
    console.log('response', response);
    return {
      game_id: response.data.game_id,
      game_name: response.data.game_name,
      game_packages_count: response.data.game_packages_count,
      game_servers_count: response.data.game_servers_count,
      serial_codes_count: response.data.serial_codes_count,
      total_count: response.data.total_count,
    };
  } catch (error: any) {
    return {
      game_id: '',
      game_name: '',
      game_packages_count: 0,
      game_servers_count: 0,
      serial_codes_count: 0,
      total_count: 0,
    };
  }
};

export const deleteGameSoft = async (
  id: string
): Promise<BaseResponse<GameData>> => {
  try {
    const response = await gameListAPI.delete(`/admin/games/${id}/soft`);
    return {
      success: true,
      message: response.data.message || 'Game deleted successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to delete game',
    };
  }
};

// Update Topup Fields
export const updateGameTopupFields = async (
  gameId: string,
  topupFields: TopupField[]
): Promise<BaseResponse<{ topup_fields: TopupField[] }>> => {
  try {
    const response = await gameListAPI.put(
      `/admin/games/${gameId}/topup-fields`,
      { topup_fields: topupFields }
    );
    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Topup fields updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update topup fields',
    };
  }
};

export interface GameTopupConfigItem {
  game_topup_type_id: string;
  supplier_ids: string[];
  topup_fields?: TopupField[];
  /** Matches `TopupGuide.id`; send `''` when API returned `topup_guides: []` */
  guide_id?: string;
  guide_title?: string;
  /** HTML; matches `TopupGuide.content_html` */
  guide_content?: string;
  /**
   * `topup_guides[].game_topup_type_id` ดิบจาก GET (เช่น type เก่าที่ไม่ตรง config ปัจจุบัน)
   * ใช้คู่กับ `guide_id` ตอนส่ง `topup_guides` ให้ API อัปเดตแถวที่ถูกต้อง
   */
  guide_api_game_topup_type_id?: string;
}

/** รูปแบบ `topup_guides` ใน form-data ตาม API admin update */
export interface TopupGuideUpdatePayload {
  id?: string;
  supplier_id: string;
  game_topup_type_id: string;
  title: string;
  content_html: string;
  steps: unknown[];
  notes: string | null;
  images: unknown[];
}

export const updateGameTopupConfig = async (
  gameId: string,
  game_topup_config: GameTopupConfigItem[],
  is_active?: boolean,
  topup_guides_rows?: TopupGuideUpdatePayload[]
): Promise<BaseResponse<GameData>> => {
  try {
    const configForApi = game_topup_config.map(
      ({
        guide_id,
        guide_title,
        guide_content,
        guide_api_game_topup_type_id,
        ...rest
      }) => rest
    );

    const topupGuidesPayload: TopupGuideUpdatePayload[] =
      configForApi.length === 0
        ? []
        : topup_guides_rows !== undefined
          ? topup_guides_rows
          : game_topup_config.map((row) => {
              const gid = row.guide_id ? String(row.guide_id).trim() : '';
              const apiType = row.guide_api_game_topup_type_id
                ? String(row.guide_api_game_topup_type_id).trim()
                : '';
              const typeForGuidePayload =
                gid && apiType
                  ? apiType
                  : String(row.game_topup_type_id ?? '').trim();
              const item: TopupGuideUpdatePayload = {
                supplier_id: row.supplier_ids?.[0] ?? '',
                game_topup_type_id: typeForGuidePayload,
                title: row.guide_title ?? '',
                content_html: row.guide_content ?? '',
                steps: [],
                notes: null,
                images: [],
              };
              if (gid) {
                item.id = gid;
              }
              return item;
            });

    const formData = new FormData();
    formData.append('game_topup_config', JSON.stringify(configForApi));
    formData.append('topup_guides', JSON.stringify(topupGuidesPayload));
    if (typeof is_active === 'boolean') {
      formData.append('is_active', String(is_active));
    }

    const response = await gameListAPI.put(`/admin/games/${gameId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return {
      success: true,
      data: unwrapGameCatalogGamePayload(response.data) ?? response.data,
      message:
        (response.data as { message?: string })?.message ||
        'Game topup config updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update game topup config',
      code: error.response?.data?.code,
      status: error.response?.data?.status ?? error.response?.status,
    };
  }
};

// Game Packages with Discounts
export interface PackageWithDiscount {
  package_id: string;
  package_name: string;
  original_price?: number;
  supplier_id?: string;
  supplier_name?: string;
  game_topup_type_id?: string;
  topup_type_name?: string;
  supplier_price: number;
  // Markup fields (global per package)
  has_markup?: boolean;
  markup_id?: string | null;
  markup_type?: 'FIXED' | 'PERCENT';
  markup_value?: number;
  markup_amount?: number;
  selling_price?: number;
  // Discount fields (per tier)
  has_discount: boolean;
  discount_id: string | null;
  discount_type: 'FIXED' | 'PERCENT';
  discount_value: number;
  discount_amount: number;
  final_price: number;
  is_active: boolean;
}

export interface GameTopupType {
  id: string;
  name: string;
  requires_link: boolean;
}

export interface GetGameTopupTypesResponse {
  success: boolean;
  data?: GameTopupType[];
  message?: string;
}

export const getGameTopupTypes =
  async (): Promise<GetGameTopupTypesResponse> => {
    try {
      const response = await gameListAPI.get('/game-topup-types/');
      const list = response.data?.data ?? response.data ?? [];
      return {
        success: true,
        data: Array.isArray(list) ? list : [],
        message: response.data?.message,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          'Failed to fetch Game Topup Types',
      };
    }
  };

export interface GetGamePackagesWithDiscountsRequest {
  game_id: string;
  agent_tier_id: string;
  supplier_ids?: string | string[] | null;
  topup_type_id?: string;
}

export interface GetGamePackagesWithDiscountsResponse {
  success: boolean;
  data?: {
    game_id: string;
    game_name: string;
    agent_tier_id: string;
    agent_tier_name: string;
    packages: PackageWithDiscount[];
  };
  message?: string;
}

export const getGamePackagesWithDiscounts = async (
  params: GetGamePackagesWithDiscountsRequest
): Promise<GetGamePackagesWithDiscountsResponse> => {
  try {
    const { game_id, agent_tier_id, supplier_ids, topup_type_id } = params;
    const body: Record<string, any> = { agent_tier_id };
    if (topup_type_id) {
      body.game_topup_type_ids = topup_type_id;
    }
    if (supplier_ids) {
      if (Array.isArray(supplier_ids)) {
        const cleaned = supplier_ids.map(String).filter(Boolean);
        if (cleaned.length) body.supplier_ids = cleaned;
      } else {
        body.supplier_ids = [supplier_ids];
      }
    }

    const response = await gameListAPI.post(
      `/admin/games/${game_id}/packages-with-discounts/search`,
      body
    );

    return {
      success: true,
      data: response.data,
      message: 'Packages with discounts fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch packages with discounts',
    };
  }
};

// Upsert Discount
export interface UpsertDiscountRequest {
  agent_tier_id: string;
  agent_tier_name: string;
  game_package_id: string;
  supplier_id?: string;
  game_topup_type_id?: string;
  discount_type: 'FIXED' | 'PERCENT';
  discount_value: number;
}

export interface UpsertDiscountResponse {
  success: boolean;
  data?: {
    id: string;
    agent_tier_id: string;
    agent_tier_name: string;
    game_package_id: string;
    game_package_name: string;
    discount_value: number;
    discount_amount: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
  message?: string;
}

export const upsertDiscount = async (
  data: UpsertDiscountRequest
): Promise<UpsertDiscountResponse> => {
  try {
    const response = await gameListAPI.post(
      '/admin/agent-tier-discounts/upsert',
      data
    );

    return {
      success: true,
      data: response.data,
      message: 'Discount updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update discount',
    };
  }
};

// Toggle Agent Tier Discount Active
export interface ToggleAgentTierDiscountActiveRequest {
  agent_tier_id: string;
  agent_tier_name: string;
  game_package_id: string;
  supplier_id?: string;
  game_topup_type_id?: string;
  discount_type: 'FIXED' | 'PERCENT';
  is_active: boolean;
}

export const toggleAgentTierDiscountActive = async (
  data: ToggleAgentTierDiscountActiveRequest
): Promise<UpsertDiscountResponse> => {
  try {
    const response = await gameListAPI.patch(
      '/admin/agent-tier-discounts/toggle-active',
      data
    );

    return {
      success: true,
      data: response.data,
      message: 'Status updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update status',
    };
  }
};

// Topup Guide
export interface TopupGuideStep {
  sequence: number;
  text: string;
  link_url?: string;
  link_text?: string;
}

export interface TopupGuideImage {
  sequence: number;
  image_url: string;
  caption?: string;
}

export interface TopupGuideData {
  id: string;
  game_id: string;
  title: string;
  steps: TopupGuideStep[];
  notes: string[];
  images: TopupGuideImage[];
}

export const getTopupGuideByGameId = async (
  gameId: string
): Promise<BaseResponse<TopupGuideData>> => {
  try {
    const response = await gameListAPI.get(
      `/customer/game-topup-guides/${gameId}`
    );
    return {
      success: true,
      data: response.data?.data || response.data,
    };
  } catch {
    return {
      success: false,
      message: 'No topup guide found',
    };
  }
};

export interface GameTopupTypeDto {
  id: string;
  name: string;
  requires_link: boolean;
  suppliers: {
    id: string;
    name: string;
    price: number;
    display_name?: string;
    description?: string;
  }[];
}

export const getGameTopupTypesByGameId = async (
  gameId: string
): Promise<BaseResponse<GameTopupTypeDto[]>> => {
  try {
    const response = await gameListAPI.get(`/games/${gameId}/topup-types`);
    return {
      success: true,
      data: response.data?.data || response.data,
    };
  } catch {
    return {
      success: false,
      message: 'No Game Topup Types found',
    };
  }
};

export interface TopUpGuideImageUploadRequest {
  image_file: File;
}

export interface TopUpGuideImageUploadResponse {
  success: boolean;
  data?: {
    file_id: string;
    file_name: string;
    url: string;
    public_url: string;
    size: number;
    content_type: string;
    uploaded_at: string;
  };
  message?: string;
}

export const uploadTopUpGuideImage = async (
  data: TopUpGuideImageUploadRequest
): Promise<TopUpGuideImageUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append(
      'image_file',
      await compressImageToWebP(data.image_file, { maxDimension: 1024 })
    );
    const response = await gameListAPI.post(
      '/admin/game-topup-guides/upload-image',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return {
      success: true,
      data: response.data?.data || response.data,
      message: response.data?.message || 'Image uploaded successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to upload image',
    };
  }
};

// Game Package Markups
export interface PackageWithMarkup {
  package_id: string;
  package_name: string;
  supplier_id: string;
  supplier_name: string;
  game_topup_type_id: string;
  topup_type_name: string;
  supplier_price: number;
  has_markup: boolean;
  markup_id: string | null;
  markup_type: 'FIXED' | 'PERCENT';
  markup_value: number;
  markup_amount: number;
  selling_price: number;
  is_active: boolean;
}

export interface GetGamePackagesWithMarkupsRequest {
  game_id: string;
  supplier_ids?: string[];
  topup_type_id?: string;
}

export interface GetGamePackagesWithMarkupsResponse {
  success: boolean;
  data?: {
    game_id: string;
    game_name: string;
    packages: PackageWithMarkup[];
  };
  message?: string;
}

export const getGamePackagesWithMarkups = async (
  params: GetGamePackagesWithMarkupsRequest
): Promise<GetGamePackagesWithMarkupsResponse> => {
  try {
    const { game_id, supplier_ids, topup_type_id } = params;
    const body: Record<string, any> = {};
    if (topup_type_id) {
      body.game_topup_type_ids = topup_type_id;
    }
    if (supplier_ids && supplier_ids.length > 0) {
      body.supplier_ids = supplier_ids;
    }
    const response = await gameListAPI.post(
      `/admin/games/${game_id}/packages-with-markups/search`,
      body
    );
    return {
      success: true,
      data: response.data,
      message: 'Packages with markups fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch packages with markups',
    };
  }
};

export interface UpsertMarkupRequest {
  game_package_id: string;
  supplier_id: string;
  game_topup_type_id: string;
  markup_type: 'FIXED' | 'PERCENT';
  markup_value: number;
}

export interface UpsertMarkupResponse {
  success: boolean;
  data?: {
    id: string;
    game_package_id: string;
    supplier_id: string;
    game_topup_type_id: string;
    markup_type: string;
    markup_value: number;
    is_active: boolean;
  };
  message?: string;
}

export const upsertMarkup = async (
  data: UpsertMarkupRequest
): Promise<UpsertMarkupResponse> => {
  try {
    const response = await gameListAPI.post(
      '/admin/game-package-markups/upsert',
      data
    );
    return {
      success: true,
      data: response.data,
      message: 'Markup updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update markup',
    };
  }
};

export interface ToggleMarkupActiveRequest {
  game_package_id: string;
  supplier_id: string;
  game_topup_type_id: string;
  is_active: boolean;
}

export const toggleMarkupActive = async (
  data: ToggleMarkupActiveRequest
): Promise<UpsertMarkupResponse> => {
  try {
    const response = await gameListAPI.patch(
      '/admin/game-package-markups/toggle-active',
      data
    );
    return {
      success: true,
      data: response.data,
      message: 'Status updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update status',
    };
  }
};

export interface UpdateSupplierPriceRequest {
  game_package_id: string;
  supplier_id: string;
  game_topup_type_id: string;
  price: number;
}

export const updateSupplierPrice = async (
  data: UpdateSupplierPriceRequest
): Promise<UpsertMarkupResponse> => {
  try {
    await gameListAPI.patch('/admin/game-package-markups/supplier-price', data);
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update supplier price',
    };
  }
};

export default gameListAPI;
